/**
 * Authoritative Cursor Soccer simulation — shared by the MP server and the client (Local).
 * Fixed-timestep physics so the ball is independent of FPS.
 */

import {
  SOCCER_VARIANT_NORMAL,
  parseSoccerVariant,
  soccerVariantConfig,
  type SoccerPickupKind,
  type SoccerVariantConfig,
  type SoccerVariantId,
} from "./soccer-variant.js";

export type { SoccerVariantId, SoccerPickupKind } from "./soccer-variant.js";
export { soccerVariantConfig, parseSoccerVariant, SOCCER_VARIANT_NORMAL, SOCCER_VARIANT_MEGA } from "./soccer-variant.js";

/** NORMAL defaults (legacy exports). Prefer `sim.cfg` / `soccerVariantConfig`. */
export const SOCCER_W = SOCCER_VARIANT_NORMAL.w;
export const SOCCER_H = SOCCER_VARIANT_NORMAL.h;
export const SOCCER_BALL_R = SOCCER_VARIANT_NORMAL.ballR;
export const SOCCER_CURSOR_R = SOCCER_VARIANT_NORMAL.cursorR;
export const SOCCER_MATCH_SEC = SOCCER_VARIANT_NORMAL.matchSec;
export const SOCCER_GOAL_FREEZE_SEC = 3;
export const SOCCER_COUNTDOWN_SEC = 4;
export const SOCCER_AFK_WARN_SEC = 45;
export const SOCCER_AFK_FORFEIT_SEC = 60;
export const SOCCER_RECONNECT_SEC = 20;
export const SOCCER_TEAM_BLUE = "#3d8cff";
export const SOCCER_TEAM_ORANGE = "#ff8a2b";
export const SOCCER_BLUE_FIELD = "#0c1e38";
export const SOCCER_ORANGE_FIELD = "#5a2808";

const WALL_REST = 0.72;
const POST_REST = 0.78;
const SPIN_DECAY = 1.85;
const REPLAY_PRE_GOAL_SEC = 3;
const REPLAY_POST_GOAL_SEC = 2;
const FIXED_DT = 1 / 120;
const MAX_ACCUM = 0.08;
const REPLAY_BUFFER_SEC = 12;
const REPLAY_HZ = 60;

export type SoccerTeam = "blue" | "orange";
export type SoccerPhase =
  | "countdown"
  | "playing"
  | "goal_freeze"
  | "replay"
  | "overtime_countdown"
  | "overtime"
  | "ended";

export type SoccerMessageKind =
  | "goal"
  | "save"
  | "shot"
  | "post"
  | "great_shot"
  | "close_call"
  | "time_warn"
  | "overtime"
  | "pickup_max"
  | "pickup_small";

export type SoccerContactKind = "hit" | "wall" | "post" | "near";

export type SoccerPlayerInput = {
  id: string;
  name: string;
  team: SoccerTeam;
  x: number;
  y: number;
  vx: number;
  vy: number;
  connected: boolean;
  lastMoveAt: number;
  /** MEGA: hold boost (PC left mouse / mobile toggle). */
  boost?: boolean;
};

export type SoccerPlayerState = {
  id: string;
  name: string;
  team: SoccerTeam;
  x: number;
  y: number;
  vx: number;
  vy: number;
  connected: boolean;
  lastMoveAt: number;
  goals: number;
  shots: number;
  saves: number;
  touches: number;
  posts: number;
  boostCharge: number;
  boostActive: boolean;
  desiredX: number;
  desiredY: number;
  /** Prior frame position for swept ball contact. */
  prevX: number;
  prevY: number;
};

export type SoccerPickupState = {
  id: string;
  kind: SoccerPickupKind;
  x: number;
  y: number;
  /** null = available; otherwise sim time when it respawns. */
  respawnAt: number | null;
};

export type SoccerBallState = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Side-spin (rad/s); Magnus curve while moving. */
  spin: number;
  /** Visual roll angle (rad). */
  angle: number;
};

export type SoccerEvent = {
  kind: SoccerMessageKind | SoccerContactKind;
  t: number;
  x: number;
  y: number;
  intensity: number;
  playerId?: string;
  team?: SoccerTeam;
  text?: string;
};

export type SoccerReplayFrame = {
  t: number;
  ball: SoccerBallState;
  players: Array<{ id: string; x: number; y: number; team: SoccerTeam }>;
  highlight: boolean;
};

export type SoccerReplayClip = {
  frames: SoccerReplayFrame[];
  duration: number;
};

export type SoccerSnapshot = {
  phase: SoccerPhase;
  variant: SoccerVariantId;
  ball: SoccerBallState;
  players: SoccerPlayerState[];
  pickups: SoccerPickupState[];
  scoreBlue: number;
  scoreOrange: number;
  timeLeft: number;
  overtimeTime: number;
  countdown: number;
  goVisible: boolean;
  showFiveMinutes: boolean;
  fiveMinutesAtTop: boolean;
  lastGoalTeam: SoccerTeam | null;
  lastGoalId: number;
  events: SoccerEvent[];
  messages: Array<{ kind: SoccerMessageKind; text: string; until: number }>;
  timeBanner: { text: string; until: number } | null;
  replay: SoccerReplayClip | null;
  replayT: number;
  replayAwaitingNames?: string[];
  winnerTeam: SoccerTeam | null;
  forfeitReason: string | null;
  afkWarned: string[];
  paused: boolean;
};

export type SoccerSimOptions = {
  allowTeamChange: boolean;
  compete: boolean;
  variant?: SoccerVariantId;
  now?: number;
};

function clamp(v: number, a: number, b: number): number {
  return Math.max(a, Math.min(b, v));
}

function len(x: number, y: number): number {
  return Math.hypot(x, y);
}

function norm(x: number, y: number): { x: number; y: number } {
  const l = len(x, y);
  if (l < 1e-8) return { x: 0, y: 0 };
  return { x: x / l, y: y / l };
}

/** Keep cursor center outside the ball (world units). */
export function separateCursorFromBall(
  cx: number,
  cy: number,
  bx: number,
  by: number,
  cursorR = SOCCER_CURSOR_R,
  ballR = SOCCER_BALL_R,
): { x: number; y: number } {
  let dx = cx - bx;
  let dy = cy - by;
  let dist = len(dx, dy);
  const min = cursorR + ballR;
  if (dist >= min) return { x: cx, y: cy };
  if (dist < 1e-6) {
    dx = 1;
    dy = 0;
    dist = 1;
  }
  const nx = dx / dist;
  const ny = dy / dist;
  return { x: bx + nx * min, y: by + ny * min };
}

export function soccerGoalRect(
  team: SoccerTeam,
  cfg: SoccerVariantConfig = SOCCER_VARIANT_NORMAL,
): { x: number; y: number; w: number; h: number } {
  const y = (cfg.h - cfg.goalMouthH) / 2;
  if (team === "blue") return { x: -cfg.goalDepth, y, w: cfg.goalDepth, h: cfg.goalMouthH };
  return { x: cfg.w, y, w: cfg.goalDepth, h: cfg.goalMouthH };
}

/** Painted goal mouth (extends into the pitch for visibility). */
export function soccerGoalVisualRect(
  team: SoccerTeam,
  cfg: SoccerVariantConfig = SOCCER_VARIANT_NORMAL,
): { x: number; y: number; w: number; h: number } {
  const y = (cfg.h - cfg.goalMouthH) / 2;
  const inset = cfg.goalInset;
  if (team === "blue") return { x: 0, y, w: inset, h: cfg.goalMouthH };
  return { x: cfg.w - inset, y, w: inset, h: cfg.goalMouthH };
}

export function soccerPosts(
  cfg: SoccerVariantConfig = SOCCER_VARIANT_NORMAL,
): Array<{ x: number; y: number; r: number }> {
  const y1 = (cfg.h - cfg.goalMouthH) / 2;
  const y2 = y1 + cfg.goalMouthH;
  return [
    { x: 0, y: y1, r: cfg.postR },
    { x: 0, y: y2, r: cfg.postR },
    { x: cfg.w, y: y1, r: cfg.postR },
    { x: cfg.w, y: y2, r: cfg.postR },
  ];
}

export function soccerKickoffPos(
  team: SoccerTeam,
  slot: number,
  teamSize: number,
  cfg: SoccerVariantConfig = SOCCER_VARIANT_NORMAL,
): { x: number; y: number } {
  const x = team === "blue" ? cfg.w * 0.28 : cfg.w * 0.72;
  if (teamSize <= 1) return { x, y: cfg.h * 0.5 };
  const spread = cfg.kickoffSpread;
  const t = teamSize === 1 ? 0.5 : slot / (teamSize - 1);
  return { x, y: cfg.h * 0.5 + (t - 0.5) * spread * 2 };
}

export function assignRandomTeams(ids: string[]): Record<string, SoccerTeam> {
  const shuffled = [...ids];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = shuffled[i]!;
    shuffled[i] = shuffled[j]!;
    shuffled[j] = tmp;
  }
  const out: Record<string, SoccerTeam> = {};
  if (shuffled.length <= 2) {
    if (shuffled[0]) out[shuffled[0]] = Math.random() < 0.5 ? "blue" : "orange";
    if (shuffled[1]) out[shuffled[1]] = out[shuffled[0]!] === "blue" ? "orange" : "blue";
    return out;
  }
  const half = Math.ceil(shuffled.length / 2);
  shuffled.forEach((id, i) => {
    out[id] = i < half ? "blue" : "orange";
  });
  if (shuffled.length === 4) {
    out[shuffled[0]!] = "blue";
    out[shuffled[1]!] = "blue";
    out[shuffled[2]!] = "orange";
    out[shuffled[3]!] = "orange";
  }
  return out;
}

export class SoccerSim {
  readonly allowTeamChange: boolean;
  readonly compete: boolean;
  readonly cfg: SoccerVariantConfig;
  readonly variant: SoccerVariantId;
  phase: SoccerPhase = "countdown";
  ball: SoccerBallState = { x: SOCCER_W / 2, y: SOCCER_H / 2, vx: 0, vy: 0, spin: 0, angle: 0 };
  players = new Map<string, SoccerPlayerState>();
  pickups: SoccerPickupState[] = [];
  scoreBlue = 0;
  scoreOrange = 0;
  timeLeft = SOCCER_MATCH_SEC;
  overtimeTime = 0;
  countdown = SOCCER_COUNTDOWN_SEC;
  goVisible = false;
  showFiveMinutes = false;
  fiveMinutesAtTop = false;
  lastGoalTeam: SoccerTeam | null = null;
  lastGoalId = 0;
  paused = false;
  winnerTeam: SoccerTeam | null = null;
  forfeitReason: string | null = null;
  events: SoccerEvent[] = [];
  messages: Array<{ kind: SoccerMessageKind; text: string; until: number }> = [];
  timeBanner: { text: string; until: number } | null = null;
  replay: SoccerReplayClip | null = null;
  replayT = 0;
  afkWarned = new Set<string>();
  private accum = 0;
  private simT = 0;
  private goalFreezeT = 0;
  private lastShotAt = -10;
  private lastSaveAt = -10;
  private lastPostAt = -10;
  private lastCloseAt = -10;
  private lastGreatAt = -10;
  private lastHitAt = -10;
  private minuteMarks = new Set<number>();
  private warned30 = false;
  private lastCountdownWhole = 4;
  private replayBuffer: SoccerReplayFrame[] = [];
  private highlightTimes: number[] = [];
  private lastGoalSimT = 0;
  private pendingEvents: SoccerEvent[] = [];
  private scoringLocked = false;

  constructor(opts: SoccerSimOptions) {
    this.allowTeamChange = opts.allowTeamChange;
    this.compete = opts.compete;
    this.variant = parseSoccerVariant(opts.variant);
    this.cfg = soccerVariantConfig(this.variant);
    this.timeLeft = this.cfg.matchSec;
    this.ball = { x: this.cfg.w / 2, y: this.cfg.h / 2, vx: 0, vy: 0, spin: 0, angle: 0 };
    this.resetPickups();
  }

  private resetPickups(): void {
    this.pickups = this.cfg.pickups.map((p) => ({
      id: p.id,
      kind: p.kind,
      x: p.x,
      y: p.y,
      respawnAt: null,
    }));
  }

  private blankPlayer(inp: SoccerPlayerInput): SoccerPlayerState {
    return {
      ...inp,
      goals: 0,
      shots: 0,
      saves: 0,
      touches: 0,
      posts: 0,
      boostCharge: 1,
      boostActive: false,
      desiredX: inp.x,
      desiredY: inp.y,
      prevX: inp.x,
      prevY: inp.y,
    };
  }

  now(): number {
    return this.simT;
  }

  setPlayers(inputs: SoccerPlayerInput[]): void {
    const seen = new Set<string>();
    const freezePos =
      this.phase === "replay" || this.phase === "countdown" || this.phase === "overtime_countdown";
    for (const inp of inputs) {
      seen.add(inp.id);
      const prev = this.players.get(inp.id);
      if (prev) {
        prev.name = inp.name;
        if (!this.compete) prev.team = inp.team;
        prev.desiredX = inp.x;
        prev.desiredY = inp.y;
        if (!freezePos) {
          if (this.cfg.speedCapped) {
            prev.boostActive = !!inp.boost && prev.boostCharge > 0.001;
          } else {
            const jumped = Math.hypot(inp.x - prev.x, inp.y - prev.y) > 0.45;
            if (jumped) {
              prev.prevX = prev.x;
              prev.prevY = prev.y;
            }
            prev.x = inp.x;
            prev.y = inp.y;
            prev.vx = inp.vx;
            prev.vy = inp.vy;
            prev.boostActive = false;
          }
        }
        prev.connected = inp.connected;
        prev.lastMoveAt = inp.lastMoveAt;
      } else {
        this.players.set(inp.id, this.blankPlayer(inp));
      }
    }
    for (const id of [...this.players.keys()]) {
      if (!seen.has(id)) this.players.delete(id);
    }
  }

  placeKickoff(): void {
    this.ball = { x: this.cfg.w / 2, y: this.cfg.h / 2, vx: 0, vy: 0, spin: 0, angle: 0 };
    const byTeam: Record<SoccerTeam, SoccerPlayerState[]> = { blue: [], orange: [] };
    for (const p of this.players.values()) byTeam[p.team].push(p);
    for (const team of ["blue", "orange"] as SoccerTeam[]) {
      const list = byTeam[team];
      list.forEach((p, i) => {
        const pos = soccerKickoffPos(team, i, list.length, this.cfg);
        p.x = pos.x;
        p.y = pos.y;
        p.desiredX = pos.x;
        p.desiredY = pos.y;
        p.prevX = pos.x;
        p.prevY = pos.y;
        p.vx = 0;
        p.vy = 0;
      });
    }
    this.ball.vx = 0;
    this.ball.vy = 0;
    this.ball.spin = 0;
  }

  startCountdown(fromOvertime = false): void {
    this.phase = fromOvertime ? "overtime_countdown" : "countdown";
    this.countdown = SOCCER_COUNTDOWN_SEC;
    this.goVisible = false;
    this.showFiveMinutes = !fromOvertime && this.timeLeft >= this.cfg.matchSec - 0.05;
    this.fiveMinutesAtTop = false;
    this.lastCountdownWhole = 4;
    this.scoringLocked = false;
    this.placeKickoff();
  }

  beginMatch(): void {
    this.phase = "playing";
    this.goVisible = false;
    this.showFiveMinutes = false;
    this.fiveMinutesAtTop = true;
    this.scoringLocked = false;
  }

  beginOvertimePlay(): void {
    this.phase = "overtime";
    this.goVisible = false;
    this.overtimeTime = 0;
    this.scoringLocked = false;
    this.pushMessage("overtime", "OVERTIME", 2.2);
    this.pendingEvents.push({
      kind: "overtime",
      t: this.simT,
      x: this.ball.x,
      y: this.ball.y,
      intensity: 1,
      text: "OVERTIME",
    });
  }

  changeTeam(playerId: string, team: SoccerTeam): boolean {
    if (this.compete || !this.allowTeamChange) return false;
    const p = this.players.get(playerId);
    if (!p || p.team === team) return false;
    p.team = team;
    const mates = [...this.players.values()].filter((o) => o.team === team);
    const pos = soccerKickoffPos(team, Math.max(0, mates.length - 1), mates.length, this.cfg);
    p.x = pos.x;
    p.y = pos.y;
    p.desiredX = pos.x;
    p.desiredY = pos.y;
    p.prevX = pos.x;
    p.prevY = pos.y;
    p.vx = 0;
    p.vy = 0;
    return true;
  }

  forfeit(losingTeam: SoccerTeam, reason: string): void {
    if (this.phase === "ended") return;
    this.winnerTeam = losingTeam === "blue" ? "orange" : "blue";
    this.forfeitReason = reason;
    this.phase = "ended";
  }

  endReplayEarly(): void {
    if (this.phase !== "replay") return;
    this.afterReplay();
  }

  step(dt: number, inputs: SoccerPlayerInput[]): SoccerEvent[] {
    this.setPlayers(inputs);
    if (this.paused && this.phase !== "ended") {
      return this.flushEvents();
    }
    this.accum = Math.min(MAX_ACCUM, this.accum + Math.max(0, dt));
    while (this.accum >= FIXED_DT) {
      this.fixedStep(FIXED_DT);
      this.accum -= FIXED_DT;
    }
    return this.flushEvents();
  }

  snapshot(): SoccerSnapshot {
    return {
      phase: this.phase,
      variant: this.variant,
      ball: { ...this.ball },
      players: [...this.players.values()].map((p) => ({ ...p })),
      pickups: this.pickups.map((p) => ({ ...p })),
      scoreBlue: this.scoreBlue,
      scoreOrange: this.scoreOrange,
      timeLeft: this.timeLeft,
      overtimeTime: this.overtimeTime,
      countdown: this.countdown,
      goVisible: this.goVisible,
      showFiveMinutes: this.showFiveMinutes,
      fiveMinutesAtTop: this.fiveMinutesAtTop,
      lastGoalTeam: this.lastGoalTeam,
      lastGoalId: this.lastGoalId,
      events: [...this.events],
      messages: this.messages.map((m) => ({ ...m })),
      timeBanner: this.timeBanner ? { ...this.timeBanner } : null,
      replay: this.replay,
      replayT: this.replayT,
      winnerTeam: this.winnerTeam,
      forfeitReason: this.forfeitReason,
      afkWarned: [...this.afkWarned],
      paused: this.paused,
    };
  }

  applySnapshot(s: SoccerSnapshot): void {
    this.phase = s.phase;
    this.ball = { ...s.ball };
    this.scoreBlue = s.scoreBlue;
    this.scoreOrange = s.scoreOrange;
    this.timeLeft = s.timeLeft;
    this.overtimeTime = s.overtimeTime;
    this.countdown = s.countdown;
    this.goVisible = s.goVisible;
    this.showFiveMinutes = s.showFiveMinutes;
    this.fiveMinutesAtTop = s.fiveMinutesAtTop;
    this.lastGoalTeam = s.lastGoalTeam;
    this.lastGoalId = s.lastGoalId;
    this.replay = s.replay;
    this.replayT = s.replayT;
    this.winnerTeam = s.winnerTeam;
    this.forfeitReason = s.forfeitReason;
    this.paused = s.paused;
    this.messages = s.messages.map((m) => ({ ...m }));
    this.timeBanner = s.timeBanner ? { ...s.timeBanner } : null;
    this.players.clear();
    for (const p of s.players) this.players.set(p.id, { ...p });
  }

  private flushEvents(): SoccerEvent[] {
    const out = this.pendingEvents;
    this.pendingEvents = [];
    this.events = out;
    for (const ev of out) {
      if (
        ev.kind === "hit" ||
        ev.kind === "post" ||
        ev.kind === "shot" ||
        ev.kind === "save" ||
        ev.kind === "near" ||
        ev.kind === "goal"
      ) {
        this.noteHighlight(ev.t);
      }
    }
    this.messages = this.messages.filter((m) => m.until > this.simT);
    if (this.timeBanner && this.timeBanner.until <= this.simT) this.timeBanner = null;
    return out;
  }

  private pushMessage(kind: SoccerMessageKind, text: string, dur: number): void {
    this.messages = this.messages.filter((m) => m.until > this.simT);
    this.messages.push({ kind, text, until: this.simT + dur });
  }

  private fixedStep(dt: number): void {
    this.simT += dt;
    if (this.phase === "ended") return;

    if (this.phase === "countdown" || this.phase === "overtime_countdown") {
      this.tickCountdown(dt);
      this.recordReplay(false);
      return;
    }

    if (this.phase === "goal_freeze") {
      this.goalFreezeT += dt;
      this.recordReplay(true);
      if (this.goalFreezeT >= SOCCER_GOAL_FREEZE_SEC) {
        this.replay = this.buildReplayClip();
        this.replayT = 0;
        this.phase = "replay";
      }
      return;
    }

    if (this.phase === "replay") {
      this.tickReplay(dt);
      return;
    }

    const live = this.phase === "playing" || this.phase === "overtime";
    if (!live) return;

    if (this.phase === "playing") this.tickClock(dt);
    else this.overtimeTime += dt;

    this.movePlayers(dt);
    this.tickBoost(dt);
    this.tickPickups();
    this.integrateBall(dt);
    this.collideCursors(dt);
    this.collideField();
    this.detectPlayEvents();
    this.recordReplay(false);

    if (!this.scoringLocked) {
      const scored = this.detectGoal();
      if (scored) this.onGoal(scored);
    }
  }

  private tickCountdown(dt: number): void {
    this.countdown -= dt;
    const whole = Math.ceil(this.countdown);
    if (whole < this.lastCountdownWhole && whole >= 1 && whole <= 3) {
      this.pendingEvents.push({
        kind: "time_warn",
        t: this.simT,
        x: this.cfg.w / 2,
        y: this.cfg.h / 2,
        intensity: 0.35,
        text: String(whole),
      });
    }
    this.lastCountdownWhole = whole;
    if (this.countdown <= 0 && !this.goVisible) {
      this.goVisible = true;
      this.showFiveMinutes = this.phase === "countdown" && this.timeLeft >= this.cfg.matchSec - 0.05;
      this.pendingEvents.push({
        kind: "time_warn",
        t: this.simT,
        x: this.cfg.w / 2,
        y: this.cfg.h / 2,
        intensity: 0.55,
        text: "GO",
      });
    }
    if (this.countdown <= -0.85) {
      if (this.phase === "overtime_countdown") this.beginOvertimePlay();
      else this.beginMatch();
    }
  }

  private tickClock(dt: number): void {
    const prev = this.timeLeft;
    this.timeLeft = Math.max(0, this.timeLeft - dt);
    const prevMin = Math.ceil(prev / 60);
    const nextMin = Math.ceil(this.timeLeft / 60);
    if (nextMin < prevMin && nextMin >= 1 && nextMin <= 4 && !this.minuteMarks.has(nextMin)) {
      this.minuteMarks.add(nextMin);
      const text = nextMin === 1 ? "1 MINUTE REMAINING" : `${nextMin} MINUTES REMAINING`;
      this.timeBanner = { text, until: this.simT + 1.6 };
      this.pendingEvents.push({
        kind: "time_warn",
        t: this.simT,
        x: this.cfg.w / 2,
        y: this.cfg.h / 2,
        intensity: 0.4,
        text,
      });
    }
    if (this.timeLeft <= 30 && prev > 30 && !this.warned30) {
      this.warned30 = true;
      this.timeBanner = { text: "30 SECONDS REMAINING", until: this.simT + 1.6 };
      this.pendingEvents.push({
        kind: "time_warn",
        t: this.simT,
        x: this.cfg.w / 2,
        y: this.cfg.h / 2,
        intensity: 0.45,
        text: "30 SECONDS REMAINING",
      });
    }
    if (this.timeLeft <= 10 && prev > 10) {
      this.timeBanner = { text: "10", until: this.simT + 0.9 };
    } else if (this.timeLeft < 10 && this.timeLeft > 0) {
      const n = Math.ceil(this.timeLeft);
      const prevN = Math.ceil(prev);
      if (n < prevN && n >= 1) {
        this.timeBanner = { text: String(n), until: this.simT + 0.85 };
        this.pendingEvents.push({
          kind: "time_warn",
          t: this.simT,
          x: this.cfg.w / 2,
          y: this.cfg.h / 2,
          intensity: 0.5,
          text: String(n),
        });
      }
    }
    if (this.timeLeft <= 0) {
      if (this.scoreBlue === this.scoreOrange) {
        this.startCountdown(true);
      } else {
        this.winnerTeam = this.scoreBlue > this.scoreOrange ? "blue" : "orange";
        this.phase = "ended";
      }
    }
  }

  private movePlayers(dt: number): void {
    if (!this.cfg.speedCapped) return;
    const cr = this.cfg.cursorR;
    const retain = Math.pow(this.cfg.coastRetain, dt * 60);
    for (const p of this.players.values()) {
      if (!p.connected) continue;
      p.prevX = p.x;
      p.prevY = p.y;
      const boosting = p.boostActive;
      const maxSp = boosting ? this.cfg.boostMaxSpeed : this.cfg.maxSpeed;
      const accel = boosting ? this.cfg.boostAccel : this.cfg.accel;
      const dx = p.desiredX - p.x;
      const dy = p.desiredY - p.y;
      const dist = len(dx, dy);
      /** Seek only while aim is ahead; otherwise pure straight-line ice coast. */
      const steering = dist > Math.max(36, maxSp * 0.05);
      if (steering) {
        const n = norm(dx, dy);
        const along = p.vx * n.x + p.vy * n.y;
        /** Kill sideways velocity so the cursor does not orbit the aim point. */
        p.vx = along * n.x;
        p.vy = along * n.y;
        const targetSp = Math.min(maxSp, dist * 3.2);
        const need = targetSp - along;
        if (need > 0) {
          const add = Math.min(need, accel * dt);
          p.vx += n.x * add;
          p.vy += n.y * add;
        } else if (along > targetSp) {
          const s = targetSp / Math.max(along, 1e-6);
          p.vx *= s;
          p.vy *= s;
        }
      } else {
        p.vx *= retain;
        p.vy *= retain;
        if (len(p.vx, p.vy) < 18) {
          p.vx = 0;
          p.vy = 0;
        }
      }
      let sp = len(p.vx, p.vy);
      if (sp > maxSp) {
        const s = maxSp / sp;
        p.vx *= s;
        p.vy *= s;
      }
      p.x = clamp(p.x + p.vx * dt, cr, this.cfg.w - cr);
      p.y = clamp(p.y + p.vy * dt, cr, this.cfg.h - cr);
    }
  }

  private tickBoost(dt: number): void {
    if (!this.cfg.speedCapped) return;
    for (const p of this.players.values()) {
      if (p.boostActive && p.boostCharge > 0) {
        p.boostCharge = Math.max(0, p.boostCharge - this.cfg.boostDrainPerSec * dt);
        if (p.boostCharge <= 0) {
          p.boostCharge = 0;
          p.boostActive = false;
        }
      }
    }
  }

  private tickPickups(): void {
    if (!this.cfg.pickups.length) return;
    for (const pick of this.pickups) {
      if (pick.respawnAt != null && this.simT >= pick.respawnAt) pick.respawnAt = null;
    }
    const live = this.phase === "playing" || this.phase === "overtime";
    if (!live) return;
    const r = this.cfg.pickupRadius;
    for (const pick of this.pickups) {
      if (pick.respawnAt != null) continue;
      for (const p of this.players.values()) {
        if (!p.connected) continue;
        if (len(p.x - pick.x, p.y - pick.y) > r + this.cfg.cursorR) continue;
        if (pick.kind === "max") p.boostCharge = 1;
        else p.boostCharge = Math.min(1, p.boostCharge + this.cfg.smallBoostGain);
        pick.respawnAt = this.simT + this.cfg.pickupRespawnSec;
        this.pendingEvents.push({
          kind: pick.kind === "max" ? "pickup_max" : "pickup_small",
          t: this.simT,
          x: pick.x,
          y: pick.y,
          intensity: pick.kind === "max" ? 1 : 0.45,
          playerId: p.id,
          team: p.team,
        });
        break;
      }
    }
  }

  private integrateBall(dt: number): void {
    let sp = len(this.ball.vx, this.ball.vy);
    if (sp > 0.4) {
      const decel = (sp > 220 ? this.cfg.ballRollDecel : this.cfg.ballRollDecelSlow) * dt;
      const ns = Math.max(0, sp - decel);
      const scale = ns / sp;
      this.ball.vx *= scale;
      this.ball.vy *= scale;
      sp = ns;
      this.ball.angle += (sp / this.cfg.ballR) * dt;
    } else {
      this.ball.vx = 0;
      this.ball.vy = 0;
      sp = 0;
    }
    if (sp > 55 && Math.abs(this.ball.spin) > 0.008) {
      const cx = -this.ball.vy / Math.max(sp, 1e-6);
      const cy = this.ball.vx / Math.max(sp, 1e-6);
      const curve = this.ball.spin * sp * this.cfg.spinCurve;
      this.ball.vx += cx * curve * dt;
      this.ball.vy += cy * curve * dt;
      sp = len(this.ball.vx, this.ball.vy);
    }
    this.ball.spin *= Math.exp(-SPIN_DECAY * dt);
    if (sp > this.cfg.maxBallSpeed) {
      const s = this.cfg.maxBallSpeed / sp;
      this.ball.vx *= s;
      this.ball.vy *= s;
    }
    this.ball.x += this.ball.vx * dt;
    this.ball.y += this.ball.vy * dt;
  }

  private collideCursors(dt: number): void {
    const min = this.cfg.ballR + this.cfg.cursorR;
    const {
      ballMass: BALL_MASS,
      cursorMass: CURSOR_MASS,
      cursorRest: CURSOR_REST,
      cursorPushGain: CURSOR_PUSH_GAIN,
      maxBallSpeed: MAX_BALL_SPEED,
      spinFromHit: SPIN_FROM_HIT,
    } = this.cfg;
    const invMassSum = 1 / BALL_MASS + 1 / CURSOR_MASS;
    const kinematicCursor = !this.cfg.speedCapped;

    for (const p of this.players.values()) {
      if (!p.connected) continue;
      const swept = this.resolveCursorBallSweep(p, min);
      if (swept) {
        const cursorSpeed = len(p.vx, p.vy);
        p.touches += 1;
        this.lastHitAt = this.simT;
        this.pendingEvents.push({
          kind: "hit",
          t: this.simT,
          x: this.ball.x,
          y: this.ball.y,
          intensity: clamp(cursorSpeed / 900, 0.2, 1),
          playerId: p.id,
          team: p.team,
        });
        this.maybeShot(p, clamp(cursorSpeed / 900, 0.2, 1), cursorSpeed);
        continue;
      }

      let dx = this.ball.x - p.x;
      let dy = this.ball.y - p.y;
      let dist = len(dx, dy);
      if (dist > min && dist < min + 28 && this.simT - this.lastHitAt > 0.18) {
        this.pendingEvents.push({
          kind: "near",
          t: this.simT,
          x: this.ball.x,
          y: this.ball.y,
          intensity: 0.2,
          playerId: p.id,
          team: p.team,
        });
      }
      if (dist > min + 0.05) continue;
      if (dist < 1e-6) {
        dx = 1;
        dy = 0;
        dist = 1;
      }
      const nx = dx / dist;
      const ny = dy / dist;
      const overlap = min - dist;
      if (overlap > 0) {
        if (kinematicCursor) {
          this.ball.x += nx * overlap;
          this.ball.y += ny * overlap;
        } else {
          const cursorShare = 0.28;
          const ballShare = 0.72;
          p.x -= nx * overlap * cursorShare;
          p.y -= ny * overlap * cursorShare;
          this.ball.x += nx * overlap * ballShare;
          this.ball.y += ny * overlap * ballShare;
          const sep = separateCursorFromBall(p.x, p.y, this.ball.x, this.ball.y, this.cfg.cursorR, this.cfg.ballR);
          p.x = sep.x;
          p.y = sep.y;
        }
      }

      const cursorSpeed = len(p.vx, p.vy);
      const relVx = this.ball.vx - p.vx;
      const relVy = this.ball.vy - p.vy;
      const velAlongN = relVx * nx + relVy * ny;
      const rest = Math.max(CURSOR_REST, kinematicCursor ? 0.55 : 0.4);
      if (velAlongN < 0) {
        const impulse = (-(1 + rest) * velAlongN) / invMassSum;
        this.ball.vx += (impulse / BALL_MASS) * nx;
        this.ball.vy += (impulse / BALL_MASS) * ny;
        if (!kinematicCursor) {
          p.vx -= (impulse / CURSOR_MASS) * nx * 0.35;
          p.vy -= (impulse / CURSOR_MASS) * ny * 0.35;
        }
      }

      /** Strong transfer: fast cursor hits launch the ball. */
      const pushInto = Math.max(0, p.vx * nx + p.vy * ny);
      const hitPower = Math.max(cursorSpeed, pushInto, Math.max(0, -velAlongN));
      const launch = hitPower * (kinematicCursor ? 1.35 : 1.15) + pushInto * 0.45;
      if (launch > 8) {
        this.ball.vx += nx * launch * (0.55 + Math.min(0.55, CURSOR_PUSH_GAIN * dt));
        this.ball.vy += ny * launch * (0.55 + Math.min(0.55, CURSOR_PUSH_GAIN * dt));
      }
      if (cursorSpeed > 4) {
        const moveDir = norm(p.vx, p.vy);
        const carry = clamp(cursorSpeed * (kinematicCursor ? 1.05 : 0.95), 0, MAX_BALL_SPEED);
        const blend = 1 - Math.exp(-(CURSOR_PUSH_GAIN + 8) * dt);
        this.ball.vx += (moveDir.x * carry - this.ball.vx) * blend * 0.72;
        this.ball.vy += (moveDir.y * carry - this.ball.vy) * blend * 0.72;
      }

      const tx = -ny;
      const ty = nx;
      const tang = relVx * tx + relVy * ty;
      this.ball.spin += tang * SPIN_FROM_HIT * 0.12 * dt * 60;
      this.ball.spin = clamp(this.ball.spin, -1.8, 1.8);
      this.ball.spin *= 0.9;

      let ballSpAfter = len(this.ball.vx, this.ball.vy);
      if (ballSpAfter > MAX_BALL_SPEED) {
        const s = MAX_BALL_SPEED / ballSpAfter;
        this.ball.vx *= s;
        this.ball.vy *= s;
        ballSpAfter = MAX_BALL_SPEED;
      }

      const incoming = Math.max(0, -velAlongN);
      p.touches += 1;
      const intensity = clamp((incoming + cursorSpeed) / 900, 0.15, 1);
      this.lastHitAt = this.simT;
      this.pendingEvents.push({
        kind: "hit",
        t: this.simT,
        x: this.ball.x,
        y: this.ball.y,
        intensity,
        playerId: p.id,
        team: p.team,
      });
      this.maybeShot(p, intensity, incoming + cursorSpeed);
    }
  }

  /** Catch fast cursor tunnels through the ball (esp. NORMAL absolute mouse). */
  private resolveCursorBallSweep(p: SoccerPlayerState, min: number): boolean {
    const ax = p.prevX;
    const ay = p.prevY;
    const bx = p.x;
    const by = p.y;
    const move = len(bx - ax, by - ay);
    if (move < 1) return false;
    const mdir = norm(bx - ax, by - ay);
    const steps = clamp(Math.ceil(move / Math.max(8, this.cfg.ballR * 0.35)), 1, 12);
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const cx = ax + (bx - ax) * t;
      const cy = ay + (by - ay) * t;
      const dx = this.ball.x - cx;
      const dy = this.ball.y - cy;
      const dist = len(dx, dy);
      if (dist >= min || dist < 1e-6) continue;
      /** Push along travel direction so a rightward tunnel never kicks the ball backward. */
      const overlap = min - dist;
      this.ball.x += mdir.x * Math.max(overlap, this.cfg.ballR * 0.15);
      this.ball.y += mdir.y * Math.max(overlap, this.cfg.ballR * 0.15);
      const sweepSpeed = move / Math.max(1 / 120, 1e-3);
      if (sweepSpeed > 10) {
        this.ball.vx += mdir.x * sweepSpeed * 0.95;
        this.ball.vy += mdir.y * sweepSpeed * 0.95;
      }
      if (!this.cfg.speedCapped) {
        const sep = separateCursorFromBall(bx, by, this.ball.x, this.ball.y, this.cfg.cursorR, this.cfg.ballR);
        p.x = sep.x;
        p.y = sep.y;
      }
      let sp = len(this.ball.vx, this.ball.vy);
      if (sp > this.cfg.maxBallSpeed) {
        const s = this.cfg.maxBallSpeed / sp;
        this.ball.vx *= s;
        this.ball.vy *= s;
      }
      return true;
    }
    return false;
  }

  private collideField(): void {
    const GOAL_MOUTH_H = this.cfg.goalMouthH;
    const GOAL_DEPTH = this.cfg.goalDepth;
    const mouthTop = (this.cfg.h - this.cfg.goalMouthH) / 2;
    const mouthBot = mouthTop + this.cfg.goalMouthH;
    const r = this.cfg.ballR;

    if (this.ball.y - r < 0) {
      this.ball.y = r;
      if (this.ball.vy < 0) {
        this.ball.vy *= -WALL_REST;
        this.wallEvent();
      }
    } else if (this.ball.y + r > this.cfg.h) {
      this.ball.y = this.cfg.h - r;
      if (this.ball.vy > 0) {
        this.ball.vy *= -WALL_REST;
        this.wallEvent();
      }
    }

    const inMouthY = this.ball.y > mouthTop + r * 0.15 && this.ball.y < mouthBot - r * 0.15;
    if (this.ball.x - r < 0 && !inMouthY) {
      this.ball.x = r;
      if (this.ball.vx < 0) {
        this.ball.vx *= -WALL_REST;
        this.wallEvent();
      }
    } else if (this.ball.x + r > this.cfg.w && !inMouthY) {
      this.ball.x = this.cfg.w - r;
      if (this.ball.vx > 0) {
        this.ball.vx *= -WALL_REST;
        this.wallEvent();
      }
    }

    /** Keep enough depth past the line for detectGoal (needs ~0.35R past the posts). */
    const netFloor = Math.min(r * 0.15, GOAL_DEPTH * 0.2);
    if (this.ball.x < 0) this.ball.x = Math.max(-GOAL_DEPTH + netFloor, this.ball.x);
    if (this.ball.x > this.cfg.w) this.ball.x = Math.min(this.cfg.w + GOAL_DEPTH - netFloor, this.ball.x);

    for (const post of soccerPosts(this.cfg)) {
      const dx = this.ball.x - post.x;
      const dy = this.ball.y - post.y;
      const dist = len(dx, dy);
      const min = r + post.r;
      if (dist < min && dist > 1e-6) {
        const n = norm(dx, dy);
        this.ball.x = post.x + n.x * min;
        this.ball.y = post.y + n.y * min;
        const rel = this.ball.vx * n.x + this.ball.vy * n.y;
        if (rel < 0) {
          this.ball.vx -= (1 + POST_REST) * rel * n.x;
          this.ball.vy -= (1 + POST_REST) * rel * n.y;
        }
        if (this.simT - this.lastPostAt > 0.25) {
          this.lastPostAt = this.simT;
          this.pendingEvents.push({
            kind: "post",
            t: this.simT,
            x: this.ball.x,
            y: this.ball.y,
            intensity: clamp(len(this.ball.vx, this.ball.vy) / 900, 0.3, 1),
          });
          this.pushMessage("post", "POST!", 1.15);
          const last = this.lastToucher();
          if (last) last.posts += 1;
        }
      }
    }
  }

  private wallEvent(): void {
    this.pendingEvents.push({
      kind: "wall",
      t: this.simT,
      x: this.ball.x,
      y: this.ball.y,
      intensity: clamp(len(this.ball.vx, this.ball.vy) / 800, 0.15, 0.85),
    });
  }

  private maybeShot(p: SoccerPlayerState, intensity: number, speed: number): void {
    const towardOrange = this.ball.vx > 80;
    const towardBlue = this.ball.vx < -80;
    const attacking =
      (p.team === "blue" && towardOrange) || (p.team === "orange" && towardBlue);
    if (!attacking) return;
    const onTarget = this.ballHeadingOnTarget(p.team === "blue" ? "orange" : "blue");
    if (onTarget && this.simT - this.lastShotAt > 1.1) {
      this.lastShotAt = this.simT;
      p.shots += 1;
      this.pendingEvents.push({
        kind: "shot",
        t: this.simT,
        x: this.ball.x,
        y: this.ball.y,
        intensity,
        playerId: p.id,
        team: p.team,
      });
      this.pushMessage("shot", "SHOT ON GOAL!", 1.05);
      if (speed > 720 && this.simT - this.lastGreatAt > 1.4) {
        this.lastGreatAt = this.simT;
        this.pushMessage("great_shot", "GREAT SHOT!", 1.1);
      }
    }
  }

  private ballHeadingOnTarget(goal: SoccerTeam): boolean {
    const mouthTop = (this.cfg.h - this.cfg.goalMouthH) / 2 + 18;
    const mouthBot = mouthTop + this.cfg.goalMouthH - 36;
    if (this.ball.y < mouthTop || this.ball.y > mouthBot) return false;
    if (goal === "orange") return this.ball.vx > 140 && this.ball.x > this.cfg.w * 0.55;
    return this.ball.vx < -140 && this.ball.x < this.cfg.w * 0.45;
  }

  private detectPlayEvents(): void {
    const speed = len(this.ball.vx, this.ball.vy);
    for (const goal of ["blue", "orange"] as SoccerTeam[]) {
      const line = goal === "blue" ? 0 : this.cfg.w;
      const approaching = goal === "blue" ? this.ball.vx < -90 : this.ball.vx > 90;
      const dist = Math.abs(this.ball.x - line);
      if (approaching && dist < 70 && speed > 180 && this.simT - this.lastCloseAt > 1.6) {
        const mouthTop = (this.cfg.h - this.cfg.goalMouthH) / 2;
        const mouthBot = mouthTop + this.cfg.goalMouthH;
        if (this.ball.y > mouthTop && this.ball.y < mouthBot) {
          this.lastCloseAt = this.simT;
          this.pendingEvents.push({
            kind: "close_call",
            t: this.simT,
            x: this.ball.x,
            y: this.ball.y,
            intensity: 0.7,
          });
          this.pushMessage("close_call", "CLOSE CALL!", 1.1);
        }
      }
    }

    if (this.simT - this.lastShotAt < 0.85) {
      const target = this.ball.vx > 0 ? "orange" : "blue";
      const defenderTeam = target;
      for (const p of this.players.values()) {
        if (p.team !== defenderTeam) continue;
        const d = len(this.ball.x - p.x, this.ball.y - p.y);
        if (d < this.cfg.ballR + this.cfg.cursorR + 10 && this.simT - this.lastSaveAt > 1.4) {
          const nowAway =
            (target === "orange" && this.ball.vx < 40) || (target === "blue" && this.ball.vx > -40);
          if (nowAway) {
            this.lastSaveAt = this.simT;
            p.saves += 1;
            this.pendingEvents.push({
              kind: "save",
              t: this.simT,
              x: this.ball.x,
              y: this.ball.y,
              intensity: 0.85,
              playerId: p.id,
              team: p.team,
            });
            this.pushMessage("save", "WHAT A SAVE!", 1.25);
          }
        }
      }
    }
  }

  private detectGoal(): SoccerTeam | null {
    const r = this.cfg.ballR;
    const mouthTop = (this.cfg.h - this.cfg.goalMouthH) / 2;
    const mouthBot = mouthTop + this.cfg.goalMouthH;
    /** Center must be in the mouth; allow a bit of ball overhang so MEGA depth still scores. */
    const inMouthY = this.ball.y >= mouthTop + r * 0.2 && this.ball.y <= mouthBot - r * 0.2;
    if (!inMouthY) return null;
    if (this.ball.x <= -r * 0.35) return "orange";
    if (this.ball.x >= this.cfg.w + r * 0.35) return "blue";
    return null;
  }

  private lastToucher(): SoccerPlayerState | null {
    let best: SoccerPlayerState | null = null;
    let bestD = Infinity;
    for (const p of this.players.values()) {
      const d = len(this.ball.x - p.x, this.ball.y - p.y);
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    return best;
  }

  private onGoal(scoringTeam: SoccerTeam): void {
    this.scoringLocked = true;
    this.lastGoalTeam = scoringTeam;
    this.lastGoalId += 1;
    if (scoringTeam === "blue") this.scoreBlue += 1;
    else this.scoreOrange += 1;
    const scorer = [...this.players.values()].find((p) => p.team === scoringTeam);
    const closest = this.lastToucher();
    if (closest && closest.team === scoringTeam) closest.goals += 1;
    else if (scorer) scorer.goals += 1;
    this.pushMessage("goal", "GOAL!", 1.8);
    this.pendingEvents.push({
      kind: "goal",
      t: this.simT,
      x: this.ball.x,
      y: this.ball.y,
      intensity: 1,
      team: scoringTeam,
      playerId: closest?.id,
    });
    if (this.phase === "overtime") {
      this.winnerTeam = scoringTeam;
      this.phase = "ended";
      return;
    }
    this.lastGoalSimT = this.simT;
    this.phase = "goal_freeze";
    this.goalFreezeT = 0;
  }

  private recordReplay(highlight: boolean): void {
    const frame: SoccerReplayFrame = {
      t: this.simT,
      ball: { ...this.ball },
      players: [...this.players.values()].map((p) => ({
        id: p.id,
        x: p.x,
        y: p.y,
        team: p.team,
      })),
      highlight,
    };
    this.replayBuffer.push(frame);
    const minT = this.simT - REPLAY_BUFFER_SEC;
    while (this.replayBuffer.length && this.replayBuffer[0]!.t < minT) this.replayBuffer.shift();
  }

  private noteHighlight(t: number): void {
    this.highlightTimes.push(t);
    const minT = this.simT - REPLAY_BUFFER_SEC;
    this.highlightTimes = this.highlightTimes.filter((x) => x >= minT);
  }

  private buildReplayClip(): SoccerReplayClip {
    const goalT = this.lastGoalSimT || this.simT;
    const start = goalT - REPLAY_PRE_GOAL_SEC;
    const end = goalT + REPLAY_POST_GOAL_SEC;
    const raw = this.replayBuffer.filter((f) => f.t >= start && f.t <= end);
    const duration = REPLAY_PRE_GOAL_SEC + REPLAY_POST_GOAL_SEC;
    const frames: SoccerReplayFrame[] = [];
    const steps = Math.max(1, Math.round(duration * REPLAY_HZ));
    for (let i = 0; i <= steps; i++) {
      const playT = i / REPLAY_HZ;
      const sampleT = start + playT;
      const frame = this.sampleReplayBuffer(raw, sampleT);
      frames.push({ ...frame, t: playT, highlight: Math.abs(sampleT - goalT) < 0.45 });
    }
    if (!frames.length && raw.length) {
      frames.push({ ...raw[raw.length - 1]!, t: 0, highlight: true });
    }
    return { frames, duration };
  }

  private sampleReplayBuffer(raw: SoccerReplayFrame[], t: number): SoccerReplayFrame {
    if (!raw.length) {
      return {
        t,
        ball: { ...this.ball },
        players: [...this.players.values()].map((p) => ({
          id: p.id,
          x: p.x,
          y: p.y,
          team: p.team,
        })),
        highlight: false,
      };
    }
    if (t <= raw[0]!.t) return { ...raw[0]!, t };
    const last = raw[raw.length - 1]!;
    if (t >= last.t) return { ...last, t };
    let i = 0;
    while (i < raw.length - 1 && raw[i + 1]!.t < t) i += 1;
    const a = raw[i]!;
    const b = raw[i + 1]!;
    const span = Math.max(1e-6, b.t - a.t);
    const u = clamp((t - a.t) / span, 0, 1);
    return {
      t,
      ball: {
        x: a.ball.x + (b.ball.x - a.ball.x) * u,
        y: a.ball.y + (b.ball.y - a.ball.y) * u,
        vx: a.ball.vx + (b.ball.vx - a.ball.vx) * u,
        vy: a.ball.vy + (b.ball.vy - a.ball.vy) * u,
        spin: (a.ball.spin ?? 0) + ((b.ball.spin ?? 0) - (a.ball.spin ?? 0)) * u,
        angle: (a.ball.angle ?? 0) + ((b.ball.angle ?? 0) - (a.ball.angle ?? 0)) * u,
      },
      players: a.players.map((pa) => {
        const pb = b.players.find((p) => p.id === pa.id) ?? pa;
        return {
          id: pa.id,
          x: pa.x + (pb.x - pa.x) * u,
          y: pa.y + (pb.y - pa.y) * u,
          team: pa.team,
        };
      }),
      highlight: a.highlight || b.highlight,
    };
  }

  private tickReplay(dt: number): void {
    if (!this.replay) {
      this.afterReplay();
      return;
    }
    this.replayT += dt;
    const frames = this.replay.frames;
    if (!frames.length || this.replayT >= this.replay.duration) {
      this.afterReplay();
      return;
    }
    let i = 0;
    while (i < frames.length - 1 && frames[i + 1]!.t <= this.replayT) i += 1;
    const a = frames[i]!;
    const b = frames[Math.min(frames.length - 1, i + 1)]!;
    const span = Math.max(1e-4, b.t - a.t);
    const u = clamp((this.replayT - a.t) / span, 0, 1);
    this.ball.x = a.ball.x + (b.ball.x - a.ball.x) * u;
    this.ball.y = a.ball.y + (b.ball.y - a.ball.y) * u;
    this.ball.vx = a.ball.vx + (b.ball.vx - a.ball.vx) * u;
    this.ball.vy = a.ball.vy + (b.ball.vy - a.ball.vy) * u;
    this.ball.spin = (a.ball.spin ?? 0) + ((b.ball.spin ?? 0) - (a.ball.spin ?? 0)) * u;
    this.ball.angle = (a.ball.angle ?? 0) + ((b.ball.angle ?? 0) - (a.ball.angle ?? 0)) * u;
    for (const pa of a.players) {
      const pb = b.players.find((p) => p.id === pa.id) ?? pa;
      const pl = this.players.get(pa.id);
      if (!pl) continue;
      pl.x = pa.x + (pb.x - pa.x) * u;
      pl.y = pa.y + (pb.y - pa.y) * u;
    }
  }

  private afterReplay(): void {
    this.replay = null;
    this.replayT = 0;
    this.replayBuffer = [];
    this.highlightTimes = [];
    this.startCountdown(false);
  }
}

export function soccerWorldToNorm(
  x: number,
  y: number,
  cfg: SoccerVariantConfig = SOCCER_VARIANT_NORMAL,
): { x: number; y: number } {
  return { x: x / cfg.w, y: y / cfg.h };
}

export function soccerNormToWorld(
  x: number,
  y: number,
  cfg: SoccerVariantConfig = SOCCER_VARIANT_NORMAL,
): { x: number; y: number } {
  return { x: x * cfg.w, y: y * cfg.h };
}
